// FILE: src/components/help/SimpleText.jsx
// Renders the plain-text/markdown-lite bodies of help articles and course
// documents as React text (never as HTML): blank line = paragraph,
// "- " = bullet, "1. " = numbered step, "## " = sub-heading, **bold**.
import React from "react";
import { Box, Link, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

// **bold** and [label](/path) links. Site paths become in-app links
// (through `linkTo`, so /help/x can point at /student/help/x); http(s)
// links open normally; anything else stays plain text.
function inline(text, linkTo) {
  const parts = String(text).split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)\s]+\))/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) return <strong key={i}>{p.slice(2, -2)}</strong>;
    const m = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(p);
    if (m) {
      const [, label, href] = m;
      if (href.startsWith("/")) {
        return (
          <Link key={i} component={RouterLink} to={linkTo ? linkTo(href) : href}>
            {label}
          </Link>
        );
      }
      if (/^https?:\/\//.test(href)) {
        return (
          <Link key={i} href={href} target="_blank" rel="noopener noreferrer">
            {label}
          </Link>
        );
      }
      return label;
    }
    return p;
  });
}

export default function SimpleText({ text, headingLevel = 3, linkTo }) {
  const blocks = String(text || "").replace(/\r/g, "").split(/\n{2,}/);
  return (
    <Box sx={{ "& > * + *": { mt: 1.5 } }}>
      {blocks.map((block, i) => {
        const lines = block.split("\n").filter((l) => l.trim());
        if (!lines.length) return null;
        if (lines[0].startsWith("## ")) {
          return (
            <Typography key={i} component={`h${headingLevel}`} sx={{ fontWeight: 650, fontSize: "1.0625rem", pt: 1 }}>
              {lines[0].slice(3)}
            </Typography>
          );
        }
        if (lines.every((l) => /^\s*-\s+/.test(l))) {
          return (
            <Box key={i} component="ul" sx={{ pl: 3, my: 0, "& li + li": { mt: 0.75 } }}>
              {lines.map((l, j) => (
                <Typography key={j} component="li">
                  {inline(l.replace(/^\s*-\s+/, ""), linkTo)}
                </Typography>
              ))}
            </Box>
          );
        }
        if (lines.every((l) => /^\s*\d+\.\s+/.test(l))) {
          return (
            <Box key={i} component="ol" sx={{ pl: 3, my: 0, "& li + li": { mt: 0.75 } }}>
              {lines.map((l, j) => (
                <Typography key={j} component="li">
                  {inline(l.replace(/^\s*\d+\.\s+/, ""), linkTo)}
                </Typography>
              ))}
            </Box>
          );
        }
        return <Typography key={i}>{inline(lines.join(" "), linkTo)}</Typography>;
      })}
    </Box>
  );
}
